import { GroupPlanType } from '../../types/plansales/plan';
import {
  GetInvoiceDetailDateInfo,
  GetInvoiceDetailItem,
  GetInvoiceDetailItemType,
} from '../../types/plansales/invoice/invoiceDetail';
import { InvoiceDescriptionStatus } from '../../types/plansales/billing';

/**
 * Invoice desc 작성 분기 처리를 위한 시멘틱한 상태 반환 (프론트에서만 씁니다)
 */
export const getInvoiceSemanticsStatus = (
  planType: GroupPlanType,
  items: GetInvoiceDetailItem[],
  detail: GetInvoiceDetailDateInfo,
  total: number,
): InvoiceDescriptionStatus => {
  const isRefunded = detail.refundDate;
  const isAboutStartPlan = items.find(
    item => item.type === GetInvoiceDetailItemType.FIRST_SUBSCRIPTION,
  );
  const isAboutRenewPlan = items.find(
    item => item.type === GetInvoiceDetailItemType.RENEW_SUBSCRIPTION,
  );
  const isAboutDowngrade = items.find(
    item => item.type === GetInvoiceDetailItemType.DOWNGRADE_SUBSCRIPTION,
  );
  const isAboutClosePlan = planType === GroupPlanType.FREE; // 현재는 플랜 종료 관련 키가 안들어오고 있음. 인보이스 발행시점에 FREE 인것을 플랜 종료로 보자 with Robert, 피오나 협의
  const notYetPaid = !detail.refundDate && !detail.succeedDate;
  // credit amount 가 양수 => 크레딧으로 갚아야 할 빚이 있는 경우
  const hasCreditDebt = items.find(
    item => item.type === GetInvoiceDetailItemType.CARRY_OVER_BALANCE && item.amount > 0,
  );
  /**
   * 조건 별 status 리턴
   * (순서 주의)
   */

  if (isAboutClosePlan) {
    // 플랜 완전 종료 후에 잔여 금액 invoice
    return InvoiceDescriptionStatus.CLOSE_PLAN_AND_NOT_YET_PAID;
  }

  if (isAboutDowngrade) {
    // downgrade plan
    return InvoiceDescriptionStatus.DOWNGRADE_WITH_MINUS_CREDIT;
  }

  if (notYetPaid) {
    if (isAboutStartPlan) {
      return InvoiceDescriptionStatus.START_NOT_YET_PAID;
    }
    if (isAboutRenewPlan) {
      return InvoiceDescriptionStatus.RENEW_NOT_YET_PAID;
    }
  }

  /**
   * credit or card or credit + card 혼합 결제 인지 체크한다.
   * 이 타입은 이 함수 내에서만 쓰고 외부로 가져가지 않습니다.
   */
  type InvoicePaymentMethod = 'onlyCard' | 'onlyCredit' | 'mixed';
  const getInvoicePaymentMethod = (items: GetInvoiceDetailItem[]): InvoicePaymentMethod => {
    const paidCreditItems = items.filter(
      item => item.type === GetInvoiceDetailItemType.CARRY_OVER_BALANCE,
    ); // 크래딧으로 혼합 결제 된 건
    // 오직 카드로만 전체 결제
    if (paidCreditItems.length === 0) {
      return 'onlyCard';
    }
    // credit 으로만 전체 결제
    if (paidCreditItems.length > 0 && total === 0) {
      return 'onlyCredit';
    }
    // 카드 + 크레딧 복합 결제
    return 'mixed';
  };
  const payMethod = getInvoicePaymentMethod(items);

  /**
   * START PLAN
   */

  if (hasCreditDebt && isAboutStartPlan) {
    // 크레딧으로 갚아야 할 빚이 있는 경우
    return InvoiceDescriptionStatus.START_PAID_BY_CARD_WITH_DEBT;
  }

  if (payMethod == 'onlyCredit' && isAboutStartPlan) {
    return isRefunded
      ? InvoiceDescriptionStatus.REFUND_START_PAID_BY_ONLY_CREDIT
      : InvoiceDescriptionStatus.START_PAID_BY_ONLY_CREDIT;
  }

  if (payMethod == 'mixed' && isAboutStartPlan) {
    // ex; 이전 플랜이 종료되고 크래딧이 남은 상태에서 plan upgrade 케이스
    return isRefunded
      ? InvoiceDescriptionStatus.REFUND_START_PAID_BY_MULTI_METHOD
      : InvoiceDescriptionStatus.START_PAID_BY_MULTI_METHOD;
  }

  if (payMethod == 'onlyCard' && isAboutStartPlan) {
    return isRefunded
      ? InvoiceDescriptionStatus.REFUND_START_PAID_BY_ONLY_CARD
      : InvoiceDescriptionStatus.START_PAID_BY_ONLY_CARD;
  }

  /**
   * RENEW PLAN
   */

  if (hasCreditDebt && isAboutRenewPlan) {
    // 크레딧으로 갚아야 할 빚이 있는 경우
    return InvoiceDescriptionStatus.RENEW_PAID_BY_CARD_WITH_DEBT;
  }

  if (payMethod == 'onlyCredit' && isAboutRenewPlan) {
    // renew plan (credit only)
    return isRefunded
      ? InvoiceDescriptionStatus.REFUND_RENEW_PAID_BY_ONLY_CREDIT
      : InvoiceDescriptionStatus.RENEW_PAID_BY_ONLY_CREDIT;
  }

  if (payMethod == 'mixed' && isAboutRenewPlan) {
    return isRefunded
      ? InvoiceDescriptionStatus.REFUND_RENEW_PAID_BY_MULTI_METHOD
      : InvoiceDescriptionStatus.RENEW_PAID_BY_MULTI_METHOD;
  }

  if (payMethod == 'onlyCard' && isAboutRenewPlan) {
    return isRefunded
      ? InvoiceDescriptionStatus.REFUND_RENEW_PAID_BY_ONLY_CARD
      : InvoiceDescriptionStatus.RENEW_PAID_BY_ONLY_CARD;
  }

  if (isAboutStartPlan && !isAboutRenewPlan) {
    // start plan (card only)
    return isRefunded
      ? InvoiceDescriptionStatus.REFUND_START_PAID_BY_ONLY_CARD
      : InvoiceDescriptionStatus.START_PAID_BY_ONLY_CARD;
  }

  if (!isAboutStartPlan && isAboutRenewPlan) {
    // renew plan (card only)
    return isRefunded
      ? InvoiceDescriptionStatus.REFUND_RENEW_PAID_BY_ONLY_CARD
      : InvoiceDescriptionStatus.RENEW_PAID_BY_ONLY_CARD;
  }

  // etc : contact help center
  return InvoiceDescriptionStatus.NO_CASE;
};
